<script setup lang="ts">
import type { SoftwareDeviceType, SoftwareItem, SoftwarePlatformData, SoftwareRankingData } from '~/api/software'
import { isChineseLocale } from '~/composables/useAppI18n'
import { formatSoftwareNumber, selectSoftwareRankingList } from '~/utils/software'
import areaCodeCn from '~/assets/json/areaCodeCN.json'
import areaCodeEn from '~/assets/json/areaCodeEn.json'
import globalIcon from '~/assets/icon/global.svg?url'
const closeIcon = 'https://resources1.interface003.com/web30/2026/09/14/7d5da189-ba38-4e7b-973b-31fb30333952.png'
const switchPlatformIcon =
  'https://resources1.interface003.com/web30/2026/09/16/a031277e-bb2d-45f2-8383-d0e33df273ae.png'

type RankingType = 'active' | 'download' | 'popular'
type RankingScope = 'global' | 'current'
type CountryItem = {
  countryCode?: string
  name?: string
  flag?: string
  'flag-c'?: string
}

const SOFTWARE_RANK_BADGE_IMAGES: Record<number, string> = {
  1: 'https://resources1.interface003.com/web30/2026/09/10/b6fd2280-5a67-4cb1-acc6-887f4b35a178.png',
  2: 'https://resources1.interface003.com/web30/2026/09/10/73df3796-fbd1-4e9a-a75f-6aa048b695ac.png',
  3: 'https://resources1.interface003.com/web30/2026/09/10/8494a52a-7c00-4bc7-8c54-420b02eb4cf3.png',
}
/** 热门应用数值图标。 */
const Hot_ICON = 'https://resources1.interface003.com/web30/2026/09/10/c86dc9d6-ead0-4080-87ab-9e696522237c.png'
/** 用户下载数值图标。 */
const USER_ICON = 'https://resources1.interface003.com/web30/2026/09/17/ff27ec85-754e-494a-82be-0fed70f1f7d0.png'
/** 月度活跃排行数值图标。 */
const ACTIVE_RANKING_ICON =
  'https://resources1.interface003.com/web30/2026/09/16/359c78f3-5c52-4cd5-90d8-47ec6c22ab53.png'

const { locale, localePath, t } = useAppI18n()
const { isRtl } = useRtl()
const appStore = useAppStore()
const props = withDefaults(
  defineProps<{
    visible: boolean
    rankingType: RankingType
    rankingScope: RankingScope
    initialDeviceType: SoftwareDeviceType
    loading?: boolean
    activeData?: SoftwarePlatformData<SoftwareRankingData>
    downloadData?: SoftwarePlatformData<SoftwareRankingData>
    popularData?: SoftwarePlatformData<SoftwareRankingData>
    countryName?: string
  }>(),
  { countryName: '', loading: false },
)
const emit = defineEmits<{
  close: []
}>()
const deviceType = ref<SoftwareDeviceType>(props.initialDeviceType)
const rankingScope = ref<RankingScope>(props.rankingScope)
const rankingBodyRef = ref<HTMLElement | null>(null)
let previousBodyOverflow = ''
const platformKey = computed(() => (deviceType.value === 1 ? 'ios' : 'android'))
const effectiveRankingScope = computed<RankingScope>(() =>
  props.rankingType === 'popular' ? props.rankingScope : rankingScope.value,
)
const countryGroups = computed(() => (isChineseLocale(locale) ? areaCodeCn : areaCodeEn))
const countries = computed(() => countryGroups.value.flatMap((group) => group.data as CountryItem[]))
const currentCountry = computed(() => {
  return countries.value.find((country) => String(country.countryCode) === String(appStore.CountryCode))
})
const currentCountryName = computed(
  () => currentCountry.value?.name || (isChineseLocale(locale) ? '英国' : 'United Kingdom'),
)
const currentCountryFlag = computed(
  () =>
    currentCountry.value?.['flag-c'] ||
    currentCountry.value?.flag ||
    'https://resources1.interface003.com/web30/2026/08/03/d47e1f9a-325f-43ac-af7d-027339196525.svg',
)
const availableDevices = computed<SoftwareDeviceType[]>(() => {
  const source = getRankingSource()
  if (!source) return []
  return ([1, 2] as const).filter(
    (device) =>
      selectSoftwareRankingList(source?.[device === 1 ? 'ios' : 'android'], effectiveRankingScope.value).length,
  )
})
const rankingData = computed(() => {
  const source = getRankingSource()
  return source?.[platformKey.value]
})
/** 当前排行类型下有数据的榜单范围（全球 / 本地）。 */
const availableRankingScopes = computed<RankingScope[]>(() => {
  if (props.rankingType === 'popular') return []
  return (['global', 'current'] as const).filter((scope) => Boolean(rankingData.value?.[scope]?.length))
})
const rankingList = computed(() => selectSoftwareRankingList(rankingData.value, effectiveRankingScope.value))
const title = computed(() => {
  const platformName = deviceType.value === 1 ? 'IOS' : 'Android'

  if (props.rankingType === 'popular') {
    const popularName = t('89332', ['{0}']).replace(
      '{0}',
      effectiveRankingScope.value === 'current' ? props.countryName : '',
    )
    return `${popularName} ${platformName}`
  }

  const typeName = props.rankingType === 'active' ? t('89331') : props.rankingType === 'download' ? t('89330') : ''
  return `${typeName} ${platformName}`
})
const switchPlatformText = computed(() => {
  const targetPlatform = deviceType.value === 1 ? 'Android' : 'IOS'
  return t('89936', ['{0}']).replace('{0}', targetPlatform)
})
const targetPlatformDeviceType = computed<SoftwareDeviceType>(() => (deviceType.value === 1 ? 2 : 1))
const canSwitchPlatform = computed(
  () => props.rankingType !== 'popular' && availableDevices.value.includes(targetPlatformDeviceType.value),
)
/** 当前排行类型对应的数值指标图标（热门应用用 Hot_ICON，下载用 USER_ICON，活跃用 ACTIVE_RANKING_ICON）。 */
const rankingMetricIcon = computed(() => {
  if (props.rankingType === 'popular') return Hot_ICON
  if (props.rankingType === 'active') return ACTIVE_RANKING_ICON
  return USER_ICON
})

/** 重置排行弹窗滚动区域到顶部。 */
function resetRankingScrollTop() {
  if (rankingBodyRef.value) {
    rankingBodyRef.value.scrollTop = 0
  }
}

watch([deviceType, rankingScope], () => {
  nextTick(resetRankingScrollTop)
})
watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      deviceType.value = props.initialDeviceType
      rankingScope.value = props.rankingScope
      syncDeviceType()
      syncRankingScope()
      nextTick(resetRankingScrollTop)
      if (import.meta.client) {
        previousBodyOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
      }
      return
    }

    restoreBodyOverflow()
  },
)

watch(
  availableDevices,
  () => {
    if (props.visible) syncDeviceType()
  },
  { immediate: true },
)

watch(
  availableRankingScopes,
  () => {
    if (props.visible) syncRankingScope()
  },
  { immediate: true },
)

onMounted(() => {
  document.addEventListener('keydown', handleDocumentKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleDocumentKeydown)
  restoreBodyOverflow()
})

/** 关闭排行详情弹窗。 */
function handleClose() {
  emit('close')
}

/** 按 Escape 关闭当前打开的排行弹窗。 */
function handleDocumentKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.visible) handleClose()
}

/** 弹窗关闭或卸载时恢复页面原有滚动状态。 */
function restoreBodyOverflow() {
  if (!import.meta.client) return
  document.body.style.overflow = previousBodyOverflow
}

/** 当前设备无排行数据时，自动选中第一个有数据的设备。 */
function syncDeviceType() {
  if (availableDevices.value.includes(deviceType.value)) return
  const firstAvailableDevice = availableDevices.value[0]
  if (firstAvailableDevice) deviceType.value = firstAvailableDevice
}

/** 当前榜单范围无排行数据时，自动选中第一个有数据的范围。 */
function syncRankingScope() {
  if (props.rankingType === 'popular') return
  if (availableRankingScopes.value.includes(rankingScope.value)) return
  const firstAvailableScope = availableRankingScopes.value[0]
  if (firstAvailableScope) rankingScope.value = firstAvailableScope
}

/** 根据弹窗类型选择对应的排行数据。 */
function getRankingSource() {
  if (props.rankingType === 'active') return props.activeData
  if (props.rankingType === 'download') return props.downloadData
  return props.popularData
}

/** 从平台页签入口读取设备类型。 */
function handleSelectDevice(event: Event) {
  const nextDevice = Number((event.currentTarget as HTMLElement).dataset.device)
  if (nextDevice === 1 || nextDevice === 2) deviceType.value = nextDevice
}

/** 月度活跃与月度下载弹窗中切换 iOS / Android 设备平台。 */
function handleToggleDevice() {
  if (!canSwitchPlatform.value) return
  deviceType.value = targetPlatformDeviceType.value
}

/** 从月度排行弹窗的范围页签读取全球或当前国家榜单。 */
function handleSelectRankingScope(event: Event) {
  const nextScope = (event.currentTarget as HTMLElement).dataset.rankingScope
  if (nextScope === 'global' || nextScope === 'current') rankingScope.value = nextScope
}
/** 生成首页软件详情页路径，允许详情字段为空。 */
function buildHomeSoftwareDetailsPath(item: SoftwareItem) {
  const marketId = String(item.marketId ?? '').trim()
  const appBundleId = String(item.appBundleId ?? '').trim()
  const traderCode = String(item.traderCode ?? '').trim()
  const platformSuffix = marketId === '11' ? 2 : 1
  return `/crypto-apps/${marketId}-${appBundleId}-${traderCode}-${platformSuffix}?fromType=1`
}

/** 为 div 交互入口补齐 Enter 与 Space 键盘行为。 */
function handleKeyboardActivate(event: KeyboardEvent) {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  ;(event.currentTarget as HTMLElement).click()
}

/** 返回接口排名，没有排名时使用列表位置补齐展示。 */
function getRankNumber(rankNo: number | undefined, index: number) {
  return rankNo || index + 1
}

/** 根据排名返回图片角标地址，非前三名返回空字符串。 */
function getSoftwareRankBadgeImage(rankNo: number) {
  return SOFTWARE_RANK_BADGE_IMAGES[rankNo] ?? ''
}

/** 前三名使用图片角标，其他名次继续使用文字样式。 */
function getRankClass(rankNo: number) {
  if (rankNo <= 3) return ''
  return 'text-[#7d818a]'
}

const deviceTabRefs = ref<Array<HTMLElement | null>>([])
const scopeTabRefs = ref<Array<HTMLElement | null>>([])

function setDeviceTabRef(el: Element | any, index: number) {
  if (el instanceof HTMLElement) {
    deviceTabRefs.value[index] = el
    return
  }
  deviceTabRefs.value[index] = null
}

function onDeviceTabKeydown(event: KeyboardEvent, index: number) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    ;(event.currentTarget as HTMLElement).click()
    return
  }
  const total = availableDevices.value.length
  if (total < 2) return
  if (event.key === 'Tab') {
    if (event.shiftKey && index === 0) {
      event.preventDefault()
      deviceTabRefs.value[total - 1]?.focus()
    } else if (!event.shiftKey && index === total - 1) {
      event.preventDefault()
      deviceTabRefs.value[0]?.focus()
    }
  }
}

function setScopeTabRef(el: Element | any, index: number) {
  if (el instanceof HTMLElement) {
    scopeTabRefs.value[index] = el
    return
  }
  scopeTabRefs.value[index] = null
}

function onScopeTabKeydown(event: KeyboardEvent, index: number) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    ;(event.currentTarget as HTMLElement).click()
    return
  }
  const total = availableRankingScopes.value.length
  if (total < 2) return
  if (event.key === 'Tab') {
    if (event.shiftKey && index === 0) {
      event.preventDefault()
      scopeTabRefs.value[total - 1]?.focus()
    } else if (!event.shiftKey && index === total - 1) {
      event.preventDefault()
      scopeTabRefs.value[0]?.focus()
    }
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="fixed inset-0 z-[2200] flex items-center justify-center bg-[rgba(9,20,51,0.48)] px-[16px]"
    >
      <div
        class="ranking-dialog relative flex max-h-[min(600px,calc(100dvh-32px))] w-[720px] max-w-full flex-col overflow-hidden rounded-[28px] bg-white"
        role="dialog"
        aria-modal="true"
        aria-labelledby="software-ranking-dialog-title"
      >
        <div class="shrink-0 px-[32px] pt-[32px]">
          <div
            class="ranking-dialog-header flex items-center justify-between border-b-[0.5px] border-b-[rgba(5,21,36,0.06)] pb-[16px]"
          >
            <div
              id="software-ranking-dialog-title"
              class="truncate text-[20px] leading-[28px] font-semibold text-[#292a2e]"
            >
              {{ title }}
            </div>
            <div
              class="ms-[16px] flex size-[32px] shrink-0 cursor-pointer items-center justify-center rounded-full hover:brightness-95"
              role="button"
              tabindex="0"
              :aria-label="t('storeHomeClose')"
              @click="handleClose"
              @keydown="handleKeyboardActivate"
            >
              <CommonImage :src="closeIcon" alt="" :width="32" :height="32" loading="eager" />
            </div>
          </div>
        </div>

        <div
          v-if="
            props.rankingType === 'popular'
              ? availableDevices.length > 1
              : availableRankingScopes.length > 0 || canSwitchPlatform
          "
          class="flex shrink-0 items-center justify-between px-[32px] pt-[12px] pb-[12px]"
        >
          <div class="-mx-[4px] flex pl-[2px] rtl:pl-0 rtl:pr-[2px]" role="tablist">
            <template v-if="props.rankingType === 'popular'">
              <button
                v-for="(device, index) in availableDevices"
                :key="device"
                :ref="(el) => setDeviceTabRef(el, index)"
                type="button"
                role="tab"
                tabindex="0"
                :data-device="device"
                :aria-selected="deviceType === device"
                class="ranking-dialog-tab mx-[4px] box-border flex h-[36px] shrink-0 cursor-pointer items-center overflow-visible rounded-[2px] border-[0.5px] px-[12px] text-[13px] leading-[20px]"
                :class="[
                  { 'is-rtl': isRtl, 'is-active': deviceType === device },
                  deviceType === device
                    ? 'border-[#112D73] bg-[#112D73] font-[500] text-white'
                    : 'border-[rgba(11,18,40,0.14)] bg-white text-[#292a2e] hover:bg-[#f2f3f5]/80 hover:text-[#1d2129]/80',
                ]"
                @click="handleSelectDevice"
                @keydown="onDeviceTabKeydown($event, index)"
              >
                {{ t('89336', ['{0}']).replace('{0}', device === 1 ? 'IOS' : 'Android') }}
              </button>
            </template>
            <template v-else>
              <button
                v-for="(scope, index) in availableRankingScopes"
                :key="scope"
                :ref="(el) => setScopeTabRef(el, index)"
                type="button"
                role="tab"
                tabindex="0"
                :data-ranking-scope="scope"
                :aria-selected="rankingScope === scope"
                class="ranking-dialog-tab relative mx-[4px] box-border flex h-[36px] shrink-0 cursor-pointer items-center overflow-visible rounded-[2px] border-[0.5px] px-[12px] text-[13px] leading-[20px] whitespace-nowrap"
                :class="[
                  { 'is-rtl': isRtl, 'is-active': rankingScope === scope },
                  rankingScope === scope
                    ? 'border-[#112D73] bg-[#112D73] font-[500] text-white'
                    : 'border-[rgba(11,18,40,0.14)] bg-white font-normal text-[#292a2e] hover:bg-[#f2f3f5]/80 hover:text-[#1d2129]/80',
                ]"
                @click="handleSelectRankingScope"
                @keydown="onScopeTabKeydown($event, index)"
              >
                <CommonImage
                  v-if="scope === 'global'"
                  :src="globalIcon"
                  alt=""
                  :width="16"
                  :height="16"
                  loading="eager"
                  :class="rankingScope === scope ? 'brightness-0 invert' : ''"
                />
                <CommonImage
                  v-else
                  :src="currentCountryFlag"
                  :alt="currentCountryName"
                  width="16"
                  height="16"
                  loading="eager"
                  class="h-[16px] w-[16px] shrink-0 rounded-[999px] border-[0.5px] border-solid border-[rgba(5,21,36,0.12)] object-cover"
                />
                <span class="pl-[4px] rtl:pr-[4px] rtl:pl-0">
                  {{ scope === 'global' ? t('89337') : t('89338', ['{0}']).replace('{0}', currentCountryName) }}
                </span>
              </button>
            </template>
          </div>
          <div
            v-if="canSwitchPlatform"
            role="button"
            tabindex="0"
            class="ml-[16px] flex shrink-0 cursor-pointer items-center text-[14px] leading-[22px] text-[#7d818a] transition-colors hover:text-[#112D73] rtl:mr-[16px] rtl:ml-0"
            @click="handleToggleDevice"
            @keydown="handleKeyboardActivate"
          >
            <CommonImage
              :src="switchPlatformIcon"
              alt=""
              :width="16"
              :height="16"
              loading="eager"
              class="mr-[8px] shrink-0 rtl:mr-0 rtl:ml-[8px]"
            />
            <span>{{ switchPlatformText }}</span>
          </div>
        </div>

        <div ref="rankingBodyRef" class="ranking-dialog-body min-h-0 flex-1 overflow-y-auto">
          <div v-if="loading" class="flex h-[240px] items-center justify-center" role="status">
            <div class="ranking-dialog-loading" aria-label="正在加载排行数据">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
          <div v-else-if="rankingList.length" class="ranking-dialog-list pb-[32px]">
            <div
              v-for="(item, index) in rankingList"
              :key="`${item.appBundleId}-${index}`"
              class="mr-[12px] ml-[16px] rounded-[8px] hover:bg-[#F8F8F8] rtl:mr-[16px] rtl:ml-[12px]"
            >
              <a
                :href="localePath(buildHomeSoftwareDetailsPath(item))"
                target="_blank"
                rel="noopener noreferrer"
                class="ranking-dialog-row mr-[16px] ml-[16px] flex min-h-[92px] cursor-pointer items-center border-b-[0.5px] border-b-[rgba(5,21,36,0.06)] py-[20px] rtl:mr-[16px] rtl:ml-[16px]"
                :class="{ '!border-b-0': index === rankingList.length - 1 }"
              >
                <div class="mx-[6px] flex w-[40px] shrink-0 items-center justify-center">
                  <div
                    class="flex items-center justify-center font-[500]"
                    :class="[
                      getRankNumber(item.rankNo, index) <= 3 ? 'size-[18px]' : 'w-full text-[14px] leading-[22px]',
                      getRankClass(getRankNumber(item.rankNo, index)),
                    ]"
                  >
                    <CommonImage
                      v-if="getSoftwareRankBadgeImage(getRankNumber(item.rankNo, index))"
                      :src="getSoftwareRankBadgeImage(getRankNumber(item.rankNo, index))"
                      alt=""
                      :width="18"
                      :height="18"
                      loading="eager"
                      object-fit="contain"
                    />
                    <span v-else>{{ getRankNumber(item.rankNo, index) }}</span>
                  </div>
                </div>
                <CommonImage
                  :src="item.logo"
                  :alt="item.name"
                  :width="52"
                  :height="52"
                  loading="eager"
                  class="mx-[6px] box-border shrink-0 rounded-[6px] border border-solid border-[rgba(5,21,36,0.06)]"
                />
                <div class="mx-[6px] min-w-[0px] flex-1">
                  <div class="truncate text-[16px] leading-[24px] font-[600] text-[#1d2129]" :title="item.name">
                    {{ item.name }}
                  </div>
                  <div class="mt-[4px] truncate text-[12px] leading-[17px] text-[#86909c]" :title="item.title">
                    {{ item.title }}
                  </div>
                </div>
                <div
                  v-if="Number(props.rankingType === 'download' ? item.downloadNum : item.activeValue) > 0"
                  class="ml-[6px] flex shrink-0 items-center text-[18px] leading-[26px] text-[#292a2e] rtl:mr-[6px] rtl:ml-0"
                >
                  <div class="flex items-center">
                    <CommonImage
                      :src="rankingMetricIcon"
                      alt=""
                      :width="16"
                      :height="16"
                      loading="eager"
                      class="mr-[4px] rtl:mr-0 rtl:ml-[4px]"
                    />
                    <span class="font-wiki-Medium">
                      {{ formatSoftwareNumber(props.rankingType === 'download' ? item.downloadNum : item.activeValue) }}
                    </span>
                  </div>
                </div>
              </a>
            </div>
          </div>
          <div
            v-else
            class="flex h-[240px] items-center justify-center px-[32px] pb-[32px] text-[13px] leading-[20px] text-[#8c8f97]"
          >
            {{ t('storeHomeEmpty') }}
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="less">
.ranking-dialog-body {
  scrollbar-gutter: stable;
  scrollbar-color: rgb(0 0 0 / 20%) rgb(0 0 0 / 5%);
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track,
  &::-webkit-scrollbar-thumb {
    border-radius: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgb(0 0 0 / 5%);
  }

  &::-webkit-scrollbar-thumb {
    background: rgb(0 0 0 / 20%);
  }
}

.is-active {
  &:hover:not([aria-disabled='true']) {
    background: linear-gradient(0deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), #112d73;
  }
}

.ranking-dialog-loading {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 24px;

  span {
    width: 4px;
    height: 16px;
    border-radius: 4px;
    background: #112d73;
    animation: ranking-dialog-loading 0.9s ease-in-out infinite;

    &:nth-child(2) {
      animation-delay: 0.12s;
    }

    &:nth-child(3) {
      animation-delay: 0.24s;
    }

    &:nth-child(4) {
      animation-delay: 0.36s;
    }
  }
}

@keyframes ranking-dialog-loading {
  0%,
  100% {
    opacity: 0.35;
    transform: scaleY(0.65);
  }

  50% {
    opacity: 1;
    transform: scaleY(1);
  }
}

.ranking-dialog-tab {
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(17, 45, 115, 0.3);
  }
}
</style>
